import type { ReportPdfState } from './use-report-pdf';

/**
 * GENERATE, VIEW, SHARE — M10 / S10-04.
 *
 * **THREE STATES AND NO INVENTED PROGRESS.** Idle offers Generate; generating
 * says so and refuses a second tap; ready says how many pages there are and
 * offers the two things Brandon can do with the document. There is no
 * percentage, because nothing here can honestly measure one.
 *
 * **THE COUNT IS THE POINT OF THE READY LINE.** "Report ready — 3 pages" is the
 * first moment Brandon learns what pagination actually did, and it is why
 * knowing it before he opens the file matters.
 */
export function ReportPdfActions({ pdf }: { readonly pdf: ReportPdfState }) {
  const generating = pdf.status === 'generating';

  return (
    <section className="report-pdf" aria-labelledby="report-pdf-heading">
      <h2 id="report-pdf-heading" className="sheet-label">
        PDF
      </h2>

      {pdf.status === 'ready' ? (
        <>
          <p className="report-pdf-ready" role="status">
            Report ready — {String(pdf.pageCount)} {pdf.pageCount === 1 ? 'page' : 'pages'}
          </p>
          {/* **SAVING IS EXPLICIT AND ONE-WAY FOR THESE BYTES.** Generation
              is temporary; durable history grows only when Brandon chooses it.
              Once this artifact is saved the control is inert — tapping again
              cannot create a second copy of the same report, and a genuinely
              new report comes from generating again. */}
          <div className="report-pdf-actions">
            <button
              type="button"
              className="cl-button"
              onClick={() => {
                void pdf.view();
              }}
            >
              View PDF
            </button>
            <button
              type="button"
              className="cl-button cl-button--quiet"
              onClick={() => {
                void pdf.share();
              }}
            >
              Share PDF
            </button>
            {pdf.saveStatus === 'saved' ? (
              <span className="report-pdf-saved" role="status">
                Saved
              </span>
            ) : (
              <button
                type="button"
                className="cl-button cl-button--quiet"
                disabled={pdf.saveStatus === 'saving'}
                onClick={() => {
                  void pdf.save();
                }}
              >
                {pdf.saveStatus === 'saving' ? 'Saving…' : 'Save Report'}
              </button>
            )}
          </div>

          {pdf.saveMessage ? (
            <p className="cl-error report-pdf-message" role="alert">
              {pdf.saveMessage}
            </p>
          ) : null}
          {/* Regeneration is always available: the report is his to change. */}
          <button
            type="button"
            className="cl-button cl-button--quiet report-pdf-regenerate"
            onClick={() => {
              void pdf.generate();
            }}
          >
            Generate again
          </button>
        </>
      ) : (
        <button
          type="button"
          className="cl-button"
          disabled={generating}
          onClick={() => {
            void pdf.generate();
          }}
        >
          {generating ? 'Generating PDF…' : 'Generate PDF'}
        </button>
      )}

      {/* **BAD NEWS IS SHOWN, AND IT SAYS WHAT WAS NOT CREATED.** A failed
          generation never leaves a half-made document behind. */}
      {pdf.message ? (
        <p className="cl-error report-pdf-message" role="alert">
          {pdf.message}
        </p>
      ) : null}
    </section>
  );
}
